import { ESPIONAGE_SUN_TIE_REVEAL_CHANCE, SUN_ATTACK_ETA_INTELLIGENCE_LEVEL, SUN_ATTACK_NOTIFICATION_SYSTEM_RADIUS, SUN_CONTROL_CLAIM_POPULATION, SUN_CONTROL_CLAIM_WINDOW_HOURS, SUN_CONTROL_CONCEALMENT_INTELLIGENCE_LEVEL, SUN_CONTROL_LEDGER_CAP, SUN_CONTROL_MAINTAIN_POPULATION, SUN_CONTROL_RECLAIM_IMMUNITY_HOURS, SUN_CONTROL_TOP_UP_WINDOW_HOURS, getShipDefinition, } from "@kaeliux/shared";
import { calculateDockedPopulation } from "./ships.js";
import { calculateFleetSpeed, calculateMissionFlightSeconds } from "./flightEngine.js";
import { QueueError } from "./tickEvents.js";
/**
 * Sun control: the claim → control → stronghold state machine, the garrison
 * ledger, and the visibility rules around it (sun-control-and-round-victory
 * plan, sections 1–5). Pure: every timestamp arrives as a parameter, and
 * every timestamp written is the game time an event happened (a fleet's
 * `arrivesAt`, a deadline), never when a request got round to it — Sun
 * state resolves lazily, and a late resolution must land exactly where an
 * on-time one would have.
 *
 * Two fights, kept apart on purpose:
 *   - An uncontrolled Sun is attacked by one alliance's Team planet and
 *     defended by any player's Sun Support. A win grants a CLAIM; the
 *     attack's survivors become the garrison in place.
 *   - A held Sun is defended by its garrison alone. A reclaim the holder
 *     draws or wins makes it a stronghold, never attackable again; one it
 *     loses hands the attacker a fresh claim.
 */
const HOUR_MS = 3600000;
// ---------------------------------------------------------------------------
// Garrison ledger
// ---------------------------------------------------------------------------
function shipEntries(ships) {
    return Object.entries(ships)
        .filter(([, count]) => (count ?? 0) > 0)
        .map(([key, count]) => [Number(key), count ?? 0])
        .sort(([a], [b]) => a - b);
}
function hasShips(ships) {
    return !!ships && shipEntries(ships).length > 0;
}
function compactGarrison(garrison) {
    const out = {};
    for (const [race, ships] of Object.entries(garrison)) {
        if (!ships || !hasShips(ships))
            continue;
        out[race] = Object.fromEntries(shipEntries(ships));
    }
    return out;
}
/** Total population of a garrison, each race's ships priced by that race's hangar sizes. */
export function garrisonPopulation(garrison) {
    let total = 0;
    for (const [race, ships] of Object.entries(garrison ?? {})) {
        if (ships)
            total += calculateDockedPopulation(race, ships);
    }
    return total;
}
/** Whether a garrison holds any ship at all. */
export function isGarrisonEmpty(garrison) {
    return Object.values(garrison ?? {}).every((ships) => !hasShips(ships));
}
/** `garrison` with `ships` added to `race`'s lane. */
export function addToGarrison(garrison, race, ships) {
    const lane = { ...(garrison?.[race] ?? {}) };
    for (const [shipId, count] of shipEntries(ships))
        lane[shipId] = (lane[shipId] ?? 0) + count;
    return compactGarrison({ ...garrison, [race]: lane });
}
/** `garrison` with `ships` taken out of `race`'s lane. Throws when the lane doesn't hold them. */
export function removeFromGarrison(garrison, race, ships) {
    const lane = { ...(garrison?.[race] ?? {}) };
    for (const [shipId, count] of shipEntries(ships)) {
        const held = lane[shipId] ?? 0;
        if (count > held) {
            throw new QueueError("failed-precondition", `The garrison only holds ${held} × ${getShipDefinition(race, shipId).name}`);
        }
        lane[shipId] = held - count;
    }
    return compactGarrison({ ...garrison, [race]: lane });
}
/**
 * Split `ships` into the part that fits `room` population and the rest.
 * Whole ships only, lowest ship id first, so the same input always splits
 * the same way.
 */
export function splitShipsByPopulation(race, ships, room) {
    const accepted = {};
    const leftover = {};
    let left = Math.max(0, room);
    for (const [shipId, count] of shipEntries(ships)) {
        const hangar = getShipDefinition(race, shipId).hangar;
        const take = hangar > 0 ? Math.min(count, Math.floor(left / hangar)) : count;
        if (take > 0)
            accepted[shipId] = take;
        if (count - take > 0)
            leftover[shipId] = count - take;
        left -= take * hangar;
    }
    return { accepted, leftover };
}
// ---------------------------------------------------------------------------
// Claim → control → lapse
// ---------------------------------------------------------------------------
/** A fresh hold for `allianceId` from a battle won at `at`, already controlled when the garrison reaches the claim population. */
export function newSunControl(allianceId, garrisonPopulationAtClaim, at) {
    const claim = {
        allianceId,
        status: "claimed",
        claimedAt: at,
        claimExpiresAt: new Date(at.getTime() + SUN_CONTROL_CLAIM_WINDOW_HOURS * HOUR_MS),
        stronghold: false,
    };
    return garrisonPopulationAtClaim >= SUN_CONTROL_CLAIM_POPULATION ? takeControl(claim, at) : claim;
}
function takeControl(control, at) {
    return {
        ...control,
        status: "controlled",
        controlledAt: at,
        reclaimImmuneUntil: new Date(at.getTime() + SUN_CONTROL_RECLAIM_IMMUNITY_HOURS * HOUR_MS),
    };
}
function withoutTopUp(control) {
    const { topUpExpiresAt: _dropped, ...rest } = control;
    return rest;
}
/**
 * Apply every time-based lapse due by `at`: a claim whose window closed
 * before the garrison reached the claim population, or a stronghold whose
 * garrison wasn't restored to the floor in time. The Sun returns to
 * uncontrolled (and capturable again — the stronghold flag belongs to the
 * hold, not the Sun) and the garrison is handed back to fly home.
 */
export function settleSunControl(state, at) {
    const { control } = state;
    if (!control)
        return {};
    const garrison = compactGarrison(state.garrison ?? {});
    const population = garrisonPopulation(garrison);
    if (control.status === "claimed" && at.getTime() >= control.claimExpiresAt.getTime()) {
        return { lapse: { allianceId: control.allianceId, garrison, lapsedAt: control.claimExpiresAt, reason: "claimExpired" } };
    }
    if (control.status === "controlled" && control.topUpExpiresAt) {
        if (population >= SUN_CONTROL_MAINTAIN_POPULATION)
            return { control: withoutTopUp(control), garrison };
        if (at.getTime() >= control.topUpExpiresAt.getTime()) {
            return { lapse: { allianceId: control.allianceId, garrison, lapsedAt: control.topUpExpiresAt, reason: "floorNotRestored" } };
        }
    }
    return { control, garrison };
}
/** Whether the Sun counts toward `control.allianceId`'s Suns at `at`. */
export function isSunControlledAt(state, at) {
    return settleSunControl(state, at).control?.status === "controlled";
}
/** The next moment {@link settleSunControl} could change this Sun, if any. */
export function nextSunControlDeadline(state) {
    const { control } = state;
    if (!control)
        return undefined;
    if (control.status === "claimed")
        return control.claimExpiresAt;
    return control.topUpExpiresAt;
}
/**
 * When a lapse next falls due anywhere after `at`: the earliest
 * {@link nextSunControlDeadline} across `suns`, each settled to `at` first —
 * so a deadline that already passed, or a top-up window the garrison has
 * since met, never stands in for a later one.
 */
export function earliestSunControlDeadline(suns, at) {
    let earliest;
    for (const sun of suns) {
        const settled = settleSunControl(sun, at);
        if (settled.lapse)
            continue;
        const deadline = nextSunControlDeadline(settled);
        if (deadline && (!earliest || deadline.getTime() < earliest.getTime()))
            earliest = deadline;
    }
    return earliest;
}
/**
 * Why a `sunAttack` may not be dispatched at this Sun, or `null`. Covers
 * only what Sun control adds; the inbound-attack, per-alliance lock and
 * 48h cooldown checks stay where they were — so a reclaim waits for
 * whichever of the cooldown and the reclaim immunity ends later.
 */
export function sunAttackBlockReason(gate) {
    const { control, attackerAllianceId, now } = gate;
    if (control && attackerAllianceId && control.allianceId === attackerAllianceId) {
        return "Your alliance already holds this Sun";
    }
    const contest = gate.victoryContestAllianceIds ?? [];
    if (contest.length > 0) {
        const reclaimOfContender = control?.status === "controlled" && contest.includes(control.allianceId);
        if (!reclaimOfContender) {
            return "An alliance holds seven Suns — until its countdown ends, only its Suns may be attacked";
        }
    }
    if (!control)
        return null;
    if (control.status === "claimed")
        return "Another alliance is claiming this Sun";
    if (control.stronghold)
        return "This Sun is a stronghold — it held off a reclaim and can never be attacked again";
    if (control.reclaimImmuneUntil && now.getTime() < control.reclaimImmuneUntil.getTime()) {
        return `This Sun's garrison landed recently — it can't be reclaimed for ${SUN_CONTROL_RECLAIM_IMMUNITY_HOURS} hours after landing`;
    }
    return null;
}
/** Why Sun Support may not be sent to this Sun, or `null` — individual players never help defend a held Sun. */
export function sunDefenceBlockReason(control) {
    return control ? "Sun Support can't be sent to a Sun an alliance holds — only its garrison defends it" : null;
}
/** Why a `sunGarrison` fleet may not be dispatched at this Sun, or `null`. */
export function sunGarrisonBlockReason(gate) {
    const { control, callerAllianceId } = gate;
    if (!control || !callerAllianceId || control.allianceId !== callerAllianceId) {
        return "Sun Garrison can only be sent to a Sun your alliance has claimed or controls";
    }
    if (gate.garrisonPopulation + gate.population > SUN_CONTROL_LEDGER_CAP) {
        return `A Sun's garrison holds at most ${SUN_CONTROL_LEDGER_CAP.toLocaleString("en-US")} population — ${gate.garrisonPopulation.toLocaleString("en-US")} is already there`;
    }
    return null;
}
/**
 * What a resolved Sun battle does to the Sun. `state` must already be
 * settled to `at`. At an uncontrolled Sun a win grants a claim and the
 * survivors (up to the ledger cap) convert into the garrison in place. At a
 * held Sun a draw or a garrison win is a stronghold; a loss hands the
 * attacker a fresh claim and sends the old garrison's survivors home.
 */
export function resolveSunBattleControl(input) {
    const { control, at } = input;
    const claimFor = (displaced) => {
        if (!input.attackerAllianceId) {
            return { converted: {}, attackerReturning: { ...input.attackerSurvivors }, displaced, event: "none" };
        }
        const { accepted, leftover } = splitShipsByPopulation(input.attackerRace, input.attackerSurvivors, SUN_CONTROL_LEDGER_CAP);
        const garrison = addToGarrison({}, input.attackerRace, accepted);
        const nextControl = newSunControl(input.attackerAllianceId, garrisonPopulation(garrison), at);
        return {
            control: nextControl,
            garrison,
            converted: accepted,
            attackerReturning: leftover,
            displaced,
            event: nextControl.status === "controlled" ? "controlled" : "claimed",
        };
    };
    if (!control) {
        return input.outcome === "attackerWins"
            ? claimFor()
            : { converted: {}, attackerReturning: { ...input.attackerSurvivors }, event: "none" };
    }
    if (input.outcome === "attackerWins") {
        const survivors = compactGarrison(input.garrisonSurvivors ?? {});
        return claimFor(isGarrisonEmpty(survivors) ? undefined : { allianceId: control.allianceId, garrison: survivors });
    }
    const garrison = compactGarrison(input.garrisonSurvivors ?? input.garrison ?? {});
    const population = garrisonPopulation(garrison);
    const held = { ...withoutTopUp(control), stronghold: true };
    return {
        control: population < SUN_CONTROL_MAINTAIN_POPULATION
            ? { ...held, topUpExpiresAt: new Date(at.getTime() + SUN_CONTROL_TOP_UP_WINDOW_HOURS * HOUR_MS) }
            : held,
        garrison,
        converted: {},
        attackerReturning: { ...input.attackerSurvivors },
        event: "stronghold",
    };
}
/** What a Sun battle's {@link SunBattleControlEvent} adds to the attacker's report — empty when the attacker gained nothing. */
export function sunBattleControlMessage(event) {
    if (event === "controlled")
        return " The survivors took control of the Sun.";
    if (event === "claimed") {
        return ` The survivors hold a claim on the Sun — land a garrison of ${SUN_CONTROL_CLAIM_POPULATION.toLocaleString("en-US")} population within ${SUN_CONTROL_CLAIM_WINDOW_HOURS} hours to control it.`;
    }
    return "";
}
/**
 * A `sunGarrison` fleet reaching its Sun. `state` must already be settled to
 * `at`, so a claim that lapsed before the fleet arrived turns it back.
 * Ships that fit the ledger join the garrison; reaching the claim
 * population turns a claim into control, and reaching the floor ends a
 * stronghold's top-up window.
 */
export function landSunGarrison(input) {
    const { control, at } = input;
    if (!control || !input.allianceId || control.allianceId !== input.allianceId) {
        return {
            control,
            garrison: input.garrison,
            accepted: {},
            leftover: { ...input.ships },
            outcome: "turnedBack",
            message: "Your alliance no longer holds this Sun — the fleet is returning to the Team planet.",
        };
    }
    const before = garrisonPopulation(input.garrison);
    const { accepted, leftover } = splitShipsByPopulation(input.race, input.ships, SUN_CONTROL_LEDGER_CAP - before);
    if (!hasShips(accepted)) {
        return {
            control,
            garrison: input.garrison,
            accepted,
            leftover,
            outcome: "turnedBack",
            message: "The Sun's garrison is full — the fleet is returning to the Team planet.",
        };
    }
    const garrison = addToGarrison(input.garrison, input.race, accepted);
    const population = garrisonPopulation(garrison);
    const overflow = hasShips(leftover) ? " The garrison is full — the rest of the fleet is returning to the Team planet." : "";
    if (control.status === "claimed" && population >= SUN_CONTROL_CLAIM_POPULATION) {
        return {
            control: takeControl(control, at),
            garrison,
            accepted,
            leftover,
            outcome: "tookControl",
            message: `The garrison reached ${population.toLocaleString("en-US")} population — your alliance now controls this Sun.${overflow}`,
        };
    }
    if (control.topUpExpiresAt && population >= SUN_CONTROL_MAINTAIN_POPULATION) {
        return {
            control: withoutTopUp(control),
            garrison,
            accepted,
            leftover,
            outcome: "restored",
            message: `The garrison is back above ${SUN_CONTROL_MAINTAIN_POPULATION.toLocaleString("en-US")} population — the Sun is secure.${overflow}`,
        };
    }
    return {
        control,
        garrison,
        accepted,
        leftover,
        outcome: "joined",
        message: `The ships joined the Sun's garrison (${population.toLocaleString("en-US")} population).${overflow}`,
    };
}
/**
 * Take part of a controlled Sun's garrison out to fly home. `state` must
 * already be settled. Only the surplus above the floor may leave — the
 * garrison can never be recalled below `SUN_CONTROL_MAINTAIN_POPULATION`.
 */
export function planSunGarrisonRecall(input) {
    const { control } = input;
    if (!control || !input.allianceId || control.allianceId !== input.allianceId) {
        throw new QueueError("failed-precondition", "Your alliance doesn't hold this Sun");
    }
    if (control.status !== "controlled") {
        throw new QueueError("failed-precondition", "A claim's garrison can't be recalled until it takes control of the Sun");
    }
    if (!hasShips(input.ships))
        throw new QueueError("failed-precondition", "Choose at least one ship to recall");
    const remaining = removeFromGarrison(input.garrison, input.race, input.ships);
    if (garrisonPopulation(remaining) < SUN_CONTROL_MAINTAIN_POPULATION) {
        throw new QueueError("failed-precondition", `At least ${SUN_CONTROL_MAINTAIN_POPULATION.toLocaleString("en-US")} population must stay in the garrison`);
    }
    return remaining;
}
/** Population that may be recalled from a controlled Sun's garrison right now. */
export function recallableGarrisonPopulation(state) {
    if (state.control?.status !== "controlled")
        return 0;
    return Math.max(0, garrisonPopulation(state.garrison) - SUN_CONTROL_MAINTAIN_POPULATION);
}
/**
 * The fleet that carries garrison ships home to the Team planet — a
 * recall, a lapsed hold, or the survivors of a lost reclaim. It starts
 * already `returning`, departing the Sun at `departedAt`, so it lands
 * through the normal Team planet return path. The caller adds the ships to
 * the Team planet's in-flight ledger alongside.
 */
export function planGarrisonReturnFleet(input) {
    const sunPosition = { galaxy: input.sun.galaxy, system: input.sun.system, position: 0 };
    const speed = calculateFleetSpeed(input.race, input.ships, input.sciences);
    const oneWaySeconds = calculateMissionFlightSeconds("sunGarrison", sunPosition, input.teamPlanet.coordinates, 100, speed, { flightMultiplier: input.flightMultiplier });
    return {
        userId: input.userId,
        universeId: input.universeId,
        originPlanetId: input.teamPlanet.id,
        originOwnerId: input.teamPlanet.ownerId,
        originRace: input.race,
        origin: { ...input.teamPlanet.coordinates },
        target: sunPosition,
        mission: "sunGarrison",
        status: "returning",
        ships: { ...input.ships },
        cargo: { metal: 0, minerals: 0, gas: 0, scrap: 0 },
        speedPercent: 100,
        gasCost: 0,
        oneWaySeconds,
        departedAt: input.departedAt,
        arrivesAt: input.departedAt,
        returnsAt: new Date(input.departedAt.getTime() + oneWaySeconds * 1000),
        recalled: true,
        arrivalReport: { effect: "none", message: input.message },
        createdAt: input.departedAt,
        updatedAt: input.departedAt,
    };
}
// ---------------------------------------------------------------------------
// Visibility
// ---------------------------------------------------------------------------
/** Whether a Team planet at this Intelligence level hides who controls its alliance's Suns. */
export function isSunControllerConcealed(teamPlanetIntelligence) {
    return teamPlanetIntelligence >= SUN_CONTROL_CONCEALMENT_INTELLIGENCE_LEVEL;
}
/**
 * Whether espionage at a Sun learns who controls it. An unconcealed
 * controller is public anyway. A concealed one is revealed when the
 * sender's Intelligence beats the Team planet's, on a tie
 * `ESPIONAGE_SUN_TIE_REVEAL_CHANCE` of the time, and never from below.
 */
export function resolveSunEspionageReveal(senderIntelligence, teamPlanetIntelligence, roll) {
    if (!isSunControllerConcealed(teamPlanetIntelligence))
        return true;
    if (senderIntelligence > teamPlanetIntelligence)
        return true;
    if (senderIntelligence === teamPlanetIntelligence)
        return roll < ESPIONAGE_SUN_TIE_REVEAL_CHANCE;
    return false;
}
/**
 * One viewer's view of who holds a Sun. Members of the holding alliance see
 * everything; everyone else sees that the Sun is held, by whom unless it's
 * concealed, and its public timers — never the garrison or a top-up window.
 */
export function buildSunControlView(input) {
    const { control } = input;
    if (!control)
        return undefined;
    const viewerIsController = !!input.viewerAllianceId && input.viewerAllianceId === control.allianceId;
    const concealed = isSunControllerConcealed(input.teamPlanetIntelligence);
    const showAlliance = (viewerIsController || !concealed) && input.alliance;
    return {
        status: control.status,
        concealed,
        ...(showAlliance ? { alliance: { ...input.alliance } } : {}),
        viewerIsController,
        stronghold: control.stronghold,
        ...(control.status === "claimed" ? { claimExpiresAt: control.claimExpiresAt } : {}),
        ...(control.controlledAt ? { controlledAt: control.controlledAt } : {}),
        ...(control.reclaimImmuneUntil ? { reclaimImmuneUntil: control.reclaimImmuneUntil } : {}),
        ...(viewerIsController
            ? {
                ...(control.topUpExpiresAt ? { topUpExpiresAt: control.topUpExpiresAt } : {}),
                garrison: compactGarrison(input.garrison ?? {}),
                garrisonPopulation: garrisonPopulation(input.garrison),
            }
            : {}),
    };
}
/** Whether a planet sits within the notification radius of a Sun — same galaxy only. */
export function isWithinSunAlertRadius(planet, sun) {
    return planet.galaxy === sun.galaxy && Math.abs(planet.system - sun.system) <= SUN_ATTACK_NOTIFICATION_SYSTEM_RADIUS;
}
/**
 * The inbound Sun attacks one viewer is told about. An attack on an
 * uncontrolled Sun reaches players with a planet in range; a reclaim reaches
 * every member of the holding alliance and nobody else. The attacking
 * alliance is never alerted about its own attack. The arrival time needs
 * `SUN_ATTACK_ETA_INTELLIGENCE_LEVEL` Intelligence.
 */
export function buildSunAttackAlerts(attacks, viewer) {
    const showEta = viewer.intelligence >= SUN_ATTACK_ETA_INTELLIGENCE_LEVEL;
    const alerts = [];
    for (const attack of attacks) {
        if (viewer.allianceId && attack.attackerAllianceId === viewer.allianceId)
            continue;
        let kind = null;
        if (attack.controllerAllianceId) {
            if (viewer.allianceId && attack.controllerAllianceId === viewer.allianceId)
                kind = "reclaim";
        }
        else if (viewer.planets.some((planet) => isWithinSunAlertRadius(planet, attack))) {
            kind = "attack";
        }
        if (!kind)
            continue;
        alerts.push({
            sunId: attack.sunId,
            target: { galaxy: attack.galaxy, system: attack.system, position: 0 },
            kind,
            ...(showEta && attack.attackArrivesAt ? { arrivesAt: attack.attackArrivesAt } : {}),
        });
    }
    return alerts.sort((a, b) => (a.arrivesAt?.getTime() ?? Number.MAX_SAFE_INTEGER) - (b.arrivesAt?.getTime() ?? Number.MAX_SAFE_INTEGER)
        || a.sunId.localeCompare(b.sunId));
}
//# sourceMappingURL=sunControl.js.map